import axios, { AxiosResponse } from 'axios'
import toast from 'react-hot-toast'

import type {
  ApiResponse,
  PaginatedResponse,
  LoginForm,
  Agent,
  Store,
  Conversation,
  Message,
  Customer,
  Order,
  DashboardAnalytics,
  ConversationFilters,
  MessageFilters,
  CustomerFilters,
  SendMessageForm,
  StoreSettings,
  WhatsAppProfile,
  AutomatedMessage,
  ExportType,
  ExportFormat,
  AnalyticsPeriod
} from '@/types'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001/api',
  timeout: 30000,
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      window.location.href = '/login'
    }
    
    const message = error.response?.data?.error?.message || 'An error occurred'
    if (error.response?.status !== 401) {
      toast.error(message)
    }
    
    return Promise.reject(error)
  }
)

// Auth API (with multi-account support)
export const authApi = {
  login: (data: { email: string; password: string; storeId?: string }): Promise<AxiosResponse<ApiResponse<{
    agent: Agent;
    token: string;
    organization?: any;
    stores?: any[];
    activeStoreId?: string;
  }>>> =>
    api.post('/auth/login', data),

  register: (data: any): Promise<AxiosResponse<ApiResponse<{ agent: Agent; token: string }>>> =>
    api.post('/auth/register', data),

  me: (): Promise<AxiosResponse<ApiResponse<{
    agent: Agent;
    organization?: any;
    stores?: any[];
    activeStoreId?: string;
    accessibleStoreIds?: string[];
    capabilities?: Record<string, boolean>;
  }>>> =>
    api.get('/auth/me'),

  refresh: (): Promise<AxiosResponse<ApiResponse<{ token: string }>>> =>
    api.post('/auth/refresh'),

  logout: (): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.post('/auth/logout'),

  // Switch active store (multi-account feature)
  switchStore: (data: { storeId: string | null }): Promise<AxiosResponse<ApiResponse<{
    activeStoreId: string | null;
    store?: any;
    token: string;
    message: string;
  }>>> =>
    api.post('/auth/switch-store', data),
}

// Workspace API (standalone onboarding + team management)
export const workspaceApi = {
  // Self-serve: create a new standalone workspace + its first OWNER agent
  createWorkspace: (data: {
    workspaceName: string;
    ownerName: string;
    email: string;
    password: string;
  }): Promise<AxiosResponse<ApiResponse<{
    agent: Agent;
    organization: any;
    activeStoreId: string;
    token: string;
  }>>> =>
    api.post('/workspaces', data),

  // Owner/Admin: invite/create an agent (salesperson) under a workspace
  inviteAgent: (storeId: string, data: {
    name: string;
    email: string;
    password: string;
    role?: 'ADMIN' | 'MANAGER' | 'AGENT' | 'SUPPORT';
  }): Promise<AxiosResponse<ApiResponse<{ agent: Agent }>>> =>
    api.post(`/workspaces/${storeId}/agents`, data),
}

// Store API
export const storeApi = {
  getStore: (storeId: string): Promise<AxiosResponse<ApiResponse<{ store: Store }>>> =>
    api.get(`/stores/${storeId}`),
  
  updateSettings: (storeId: string, settings: StoreSettings): Promise<AxiosResponse<ApiResponse<{ store: Store }>>> =>
    api.put(`/stores/${storeId}/settings`, { settings }),
  
  updateWhatsApp: (storeId: string, data: any): Promise<AxiosResponse<ApiResponse<{ store: Store }>>> =>
    api.put(`/stores/${storeId}/whatsapp`, data),
  
  getAnalyticsSummary: (storeId: string, period: AnalyticsPeriod = '7d'): Promise<AxiosResponse<ApiResponse<DashboardAnalytics>>> =>
    api.get(`/stores/${storeId}/analytics/summary`, { params: { period } }),
  
  getAgents: (storeId: string): Promise<AxiosResponse<ApiResponse<{ agents: Agent[] }>>> =>
    api.get(`/stores/${storeId}/agents`),
}

// Conversation API (with multi-account unified inbox)
export const conversationApi = {
  // Standard single-store conversations
  getConversations: (filters: ConversationFilters): Promise<AxiosResponse<PaginatedResponse<Conversation>>> =>
    api.get('/conversations', { params: filters }),

  getConversation: (conversationId: string, filters?: MessageFilters): Promise<AxiosResponse<ApiResponse<{ conversation: Conversation; pagination: any }>>> =>
    api.get(`/conversations/${conversationId}`, { params: filters }),

  createConversation: (data: any): Promise<AxiosResponse<ApiResponse<{ conversation: Conversation }>>> =>
    api.post('/conversations', data),

  updateConversation: (conversationId: string, data: any): Promise<AxiosResponse<ApiResponse<{ conversation: Conversation }>>> =>
    api.put(`/conversations/${conversationId}`, data),

  assignConversation: (conversationId: string, agentId: string): Promise<AxiosResponse<ApiResponse<{ conversation: Conversation }>>> =>
    api.put(`/conversations/${conversationId}/assign`, { agentId }),

  closeConversation: (conversationId: string): Promise<AxiosResponse<ApiResponse<{ conversation: Conversation }>>> =>
    api.put(`/conversations/${conversationId}/close`),

  // Unified inbox endpoints (multi-account)
  getUnifiedConversations: (filters: ConversationFilters & { storeIds?: string }): Promise<AxiosResponse<ApiResponse<{
    conversations: Conversation[];
    stores: any[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }>>> =>
    api.get('/conversations/unified', { params: filters }),

  getUnifiedStats: (params?: { storeIds?: string }): Promise<AxiosResponse<ApiResponse<{
    total: number;
    totalUnread: number;
    byStatus: { new: number; assigned: number; inProgress: number; resolved: number; closed: number };
    byStore: { storeId: string; storeName: string; conversationCount: number }[];
  }>>> =>
    api.get('/conversations/unified/stats', { params }),
}

// Message API
export const messageApi = {
  sendMessage: (data: SendMessageForm): Promise<AxiosResponse<ApiResponse<{ message: Message }>>> =>
    api.post('/messages', data),
  
  getMessages: (conversationId: string, filters?: MessageFilters): Promise<AxiosResponse<PaginatedResponse<Message>>> =>
    api.get(`/messages/conversation/${conversationId}`, { params: filters }),
  
  markAsRead: (conversationId: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.put(`/messages/conversation/${conversationId}/read`),
  
  getStats: (storeId: string, period: AnalyticsPeriod = '7d'): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/messages/stats', { params: { storeId, period } }),
  
  searchMessages: (storeId: string, query: string, page: number = 1): Promise<AxiosResponse<PaginatedResponse<Message>>> =>
    api.get('/messages/search', { params: { storeId, query, page } }),
}

// Customer API
export const customerApi = {
  getCustomers: (storeId: string, filters?: CustomerFilters): Promise<AxiosResponse<PaginatedResponse<Customer>>> =>
    api.get('/customers', { params: { storeId, ...filters } }),
  
  getCustomer: (customerId: string): Promise<AxiosResponse<ApiResponse<{ customer: Customer }>>> =>
    api.get(`/customers/${customerId}`),
  
  updateCustomer: (customerId: string, data: any): Promise<AxiosResponse<ApiResponse<{ customer: Customer }>>> =>
    api.put(`/customers/${customerId}`, data),
  
  getCustomerOrders: (customerId: string): Promise<AxiosResponse<ApiResponse<{ orders: Order[] }>>> =>
    api.get(`/customers/${customerId}/orders`),
  
  getCustomerConversations: (customerId: string): Promise<AxiosResponse<ApiResponse<{ conversations: Conversation[] }>>> =>
    api.get(`/customers/${customerId}/conversations`),
}

// WhatsApp API
export const whatsappApi = {
  sendMessage: (data: { to: string; message: string; messageType?: string }): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post('/whatsapp/send', data),
  
  getProfile: (storeId: string): Promise<AxiosResponse<ApiResponse<{ profile: WhatsAppProfile }>>> =>
    api.get(`/whatsapp/profile/${storeId}`),
  
  updateProfile: (storeId: string, data: WhatsAppProfile): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.put(`/whatsapp/profile/${storeId}`, data),
  
  getTemplates: (storeId: string): Promise<AxiosResponse<ApiResponse<{ templates: any[] }>>> =>
    api.get(`/whatsapp/templates/${storeId}`),
  
  createTemplate: (storeId: string, data: any): Promise<AxiosResponse<ApiResponse<{ template: any }>>> =>
    api.post(`/whatsapp/templates/${storeId}`, data),
}

// Shopify API
export const shopifyApi = {
  getStoreInfo: (storeId: string): Promise<AxiosResponse<ApiResponse<{ shopInfo: any }>>> =>
    api.get(`/shopify/store/${storeId}/info`),

  syncData: (storeId: string, syncType: 'all' | 'customers' | 'orders' = 'all'): Promise<AxiosResponse<ApiResponse<{ syncResults: any }>>> =>
    api.post(`/shopify/store/${storeId}/sync`, { syncType }),

  getInstallUrl: (shop: string): Promise<AxiosResponse<ApiResponse<{ installUrl: string }>>> =>
    api.get('/shopify/install-url', { params: { shop } }),
}

// Billing API
export const billingApi = {
  getPlans: (): Promise<AxiosResponse<ApiResponse<{ plans: any[] }>>> =>
    api.get('/shopify/billing/plans'),

  createCharge: (storeId: string, plan: string): Promise<AxiosResponse<ApiResponse<{ chargeId: number; confirmationUrl: string; status: string }>>> =>
    api.post('/shopify/billing/create-charge', { storeId, plan }),

  getStatus: (storeId: string): Promise<AxiosResponse<ApiResponse<{ hasSubscription: boolean; isActive: boolean; plan?: string; trialEndsOn?: string; features?: any }>>> =>
    api.get(`/shopify/billing/status/${storeId}`),

  cancelSubscription: (storeId: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.post('/shopify/billing/cancel', { storeId }),
}

// Analytics API
export const analyticsApi = {
  getDashboard: (storeId: string, period: AnalyticsPeriod = '7d'): Promise<AxiosResponse<ApiResponse<DashboardAnalytics>>> =>
    api.get(`/analytics/dashboard/${storeId}`, { params: { period } }),
  
  getConversationAnalytics: (storeId: string, period: AnalyticsPeriod = '30d'): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/analytics/conversations/${storeId}`, { params: { period } }),
  
  getMessageAnalytics: (storeId: string, period: AnalyticsPeriod = '30d'): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/analytics/messages/${storeId}`, { params: { period } }),
  
  getCustomerAnalytics: (storeId: string, period: AnalyticsPeriod = '30d'): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/analytics/customers/${storeId}`, { params: { period } }),
  
  getRevenueAnalytics: (storeId: string, period: AnalyticsPeriod = '30d'): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/analytics/revenue/${storeId}`, { params: { period } }),
  
  exportData: (storeId: string, type: ExportType, format: ExportFormat = 'csv', period: AnalyticsPeriod = '30d'): Promise<AxiosResponse<any>> =>
    api.get(`/analytics/export/${storeId}`, { 
      params: { type, format, period },
      responseType: format === 'csv' ? 'blob' : 'json'
    }),
}

// Automation API
export const automationApi = {
  getAutomations: (storeId: string): Promise<AxiosResponse<ApiResponse<{ automations: AutomatedMessage[] }>>> =>
    api.get(`/automation/${storeId}`),
  
  createAutomation: (storeId: string, data: any): Promise<AxiosResponse<ApiResponse<{ automation: AutomatedMessage }>>> =>
    api.post(`/automation/${storeId}`, data),
  
  updateAutomation: (automationId: string, data: any): Promise<AxiosResponse<ApiResponse<{ automation: AutomatedMessage }>>> =>
    api.put(`/automation/${automationId}`, data),
  
  deleteAutomation: (automationId: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.delete(`/automation/${automationId}`),
  
  testAutomation: (automationId: string, testData: any): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.post(`/automation/${automationId}/test`, testData),
}

// AI Features API
export const aiApi = {
  // Get smart reply suggestions
  getSmartReplies: (conversationId: string): Promise<AxiosResponse<ApiResponse<{ suggestions: string[]; messageId: string }>>> =>
    api.get(`/ai/conversations/${conversationId}/smart-replies`),

  // Analyze message sentiment
  analyzeMessage: (messageId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/ai/messages/${messageId}/analyze`),

  // Categorize conversation
  categorizeConversation: (conversationId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/ai/conversations/${conversationId}/categorize`),

  // Translate message
  translateMessage: (messageId: string, targetLanguage: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/ai/messages/${messageId}/translate`, { targetLanguage }),

  // Generate conversation summary
  generateSummary: (conversationId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/ai/conversations/${conversationId}/summary`),

  // Get suggested actions
  getSuggestedActions: (conversationId: string): Promise<AxiosResponse<ApiResponse<{ suggestedActions: string[] }>>> =>
    api.post(`/ai/conversations/${conversationId}/suggest-actions`),

  // Get comprehensive analysis (all AI features at once)
  getComprehensiveAnalysis: (conversationId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/ai/conversations/${conversationId}/analysis`),
}

// Workflow API
export const workflowApi = {
  getWorkflows: (storeId: string): Promise<AxiosResponse<ApiResponse<{ workflows: any[] }>>> =>
    api.get('/workflows', { params: { storeId } }),

  getWorkflow: (workflowId: string): Promise<AxiosResponse<ApiResponse<{ workflow: any }>>> =>
    api.get(`/workflows/${workflowId}`),

  createWorkflow: (data: any): Promise<AxiosResponse<ApiResponse<{ workflow: any }>>> =>
    api.post('/workflows', data),

  updateWorkflow: (workflowId: string, data: any): Promise<AxiosResponse<ApiResponse<{ workflow: any }>>> =>
    api.put(`/workflows/${workflowId}`, data),

  deleteWorkflow: (workflowId: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.delete(`/workflows/${workflowId}`),

  executeWorkflow: (workflowId: string, triggerData: any): Promise<AxiosResponse<ApiResponse<{ execution: any }>>> =>
    api.post(`/workflows/${workflowId}/execute`, { triggerData }),

  toggleWorkflow: (workflowId: string): Promise<AxiosResponse<ApiResponse<{ workflow: any }>>> =>
    api.post(`/workflows/${workflowId}/toggle`),

  getExecutions: (workflowId: string, page: number = 1, limit: number = 20): Promise<AxiosResponse<ApiResponse<{ executions: any[]; pagination: any }>>> =>
    api.get(`/workflows/${workflowId}/executions`, { params: { page, limit } }),

  getExecution: (executionId: string): Promise<AxiosResponse<ApiResponse<{ execution: any }>>> =>
    api.get(`/workflows/executions/${executionId}`),

  getAnalytics: (storeId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/workflows/analytics/${storeId}`),

  // Template API
  getTemplates: (category?: string, search?: string): Promise<AxiosResponse<ApiResponse<{ templates: any[]; categories: any[]; popularTags: any[] }>>> =>
    api.get('/workflows/templates/all', { params: { category, search } }),

  getTemplate: (templateId: string): Promise<AxiosResponse<ApiResponse<{ template: any }>>> =>
    api.get(`/workflows/templates/${templateId}`),

  createFromTemplate: (templateId: string, storeId: string, customizations?: any): Promise<AxiosResponse<ApiResponse<{ workflow: any }>>> =>
    api.post(`/workflows/templates/${templateId}/create`, { storeId, customizations }),
}

// WhatsApp Interactive Messages API
export const interactiveApi = {
  // Send interactive list message
  sendListMessage: (data: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post('/whatsapp/interactive/list', data),

  // Send reply buttons (max 3)
  sendReplyButtons: (data: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post('/whatsapp/interactive/buttons', data),

  // Send call-to-action buttons
  sendCTAButtons: (data: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post('/whatsapp/interactive/cta', data),

  // Send single product message
  sendProductMessage: (data: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post('/whatsapp/interactive/product', data),

  // Send product list message
  sendProductListMessage: (data: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post('/whatsapp/interactive/product-list', data),

  // Send flow message
  sendFlowMessage: (data: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post('/whatsapp/interactive/flow', data),

  // Send payment request
  sendPaymentRequest: (data: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post('/whatsapp/interactive/payment', data),
}

// WhatsApp Catalog API
export const catalogApi = {
  // Get all catalogs
  getCatalogs: (): Promise<AxiosResponse<ApiResponse<{ catalogs: any[] }>>> =>
    api.get('/catalog'),

  // Get catalog by ID
  getCatalog: (catalogId: string): Promise<AxiosResponse<ApiResponse<{ catalog: any }>>> =>
    api.get(`/catalog/${catalogId}`),

  // Create catalog
  createCatalog: (data: { name: string; autoSync?: boolean }): Promise<AxiosResponse<ApiResponse<{ catalog: any }>>> =>
    api.post('/catalog', data),

  // Update catalog
  updateCatalog: (catalogId: string, data: any): Promise<AxiosResponse<ApiResponse<{ catalog: any }>>> =>
    api.put(`/catalog/${catalogId}`, data),

  // Delete catalog
  deleteCatalog: (catalogId: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.delete(`/catalog/${catalogId}`),

  // Get catalog products
  getProducts: (catalogId: string): Promise<AxiosResponse<ApiResponse<{ products: any[] }>>> =>
    api.get(`/catalog/${catalogId}/products`),

  // Add product to catalog
  addProduct: (catalogId: string, data: any): Promise<AxiosResponse<ApiResponse<{ product: any }>>> =>
    api.post(`/catalog/${catalogId}/products`, data),

  // Remove product from catalog
  removeProduct: (catalogId: string, productId: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.delete(`/catalog/${catalogId}/products/${productId}`),

  // Sync store products to catalog
  syncProducts: (catalogId: string): Promise<AxiosResponse<ApiResponse<{ totalProducts: number; syncedProducts: number; failedProducts: number }>>> =>
    api.post(`/catalog/${catalogId}/sync`),

  // Get sync status
  getSyncStatus: (catalogId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/catalog/${catalogId}/sync/status`),
}

// WhatsApp Flows API
export const flowsApi = {
  // Get all flows
  getFlows: (): Promise<AxiosResponse<ApiResponse<{ flows: any[] }>>> =>
    api.get('/flows'),

  // Get flow by ID
  getFlow: (flowId: string): Promise<AxiosResponse<ApiResponse<{ flow: any }>>> =>
    api.get(`/flows/${flowId}`),

  // Create flow
  createFlow: (data: any): Promise<AxiosResponse<ApiResponse<{ flow: any }>>> =>
    api.post('/flows', data),

  // Update flow
  updateFlow: (flowId: string, data: any): Promise<AxiosResponse<ApiResponse<{ flow: any }>>> =>
    api.put(`/flows/${flowId}`, data),

  // Delete flow
  deleteFlow: (flowId: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.delete(`/flows/${flowId}`),

  // Get flow responses
  getResponses: (flowId: string): Promise<AxiosResponse<ApiResponse<{ responses: any[] }>>> =>
    api.get(`/flows/${flowId}/responses`),

  // Get flow analytics
  getAnalytics: (flowId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/flows/${flowId}/analytics`),
}

// WhatsApp Payments API
export const paymentsApi = {
  // Get all payments
  getPayments: (params?: { status?: string; customerId?: string; orderId?: string; limit?: number; offset?: number }): Promise<AxiosResponse<ApiResponse<{ payments: any[]; total: number; limit: number; offset: number }>>> =>
    api.get('/payments', { params }),

  // Get payment by ID
  getPayment: (paymentId: string): Promise<AxiosResponse<ApiResponse<{ payment: any }>>> =>
    api.get(`/payments/${paymentId}`),

  // Update payment status
  updatePayment: (paymentId: string, data: { status?: string; errorMessage?: string }): Promise<AxiosResponse<ApiResponse<{ payment: any }>>> =>
    api.put(`/payments/${paymentId}`, data),

  // Get payment analytics summary
  getAnalyticsSummary: (params?: { startDate?: string; endDate?: string }): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/payments/analytics/summary', { params }),

  // Get daily payment analytics
  getDailyAnalytics: (params?: { startDate?: string; endDate?: string; days?: number }): Promise<AxiosResponse<ApiResponse<{ dailyPayments: any[] }>>> =>
    api.get('/payments/analytics/daily', { params }),
}

// AI-Powered Interactive Features API
export const aiInteractiveApi = {
  // Auto-generate interactive message from products
  generateMessageFromProducts: (data: {
    productIds: string[];
    tone?: 'professional' | 'casual' | 'enthusiastic';
    targetAudience?: string;
    includeOffers?: boolean;
    customerPhone?: string;
  }): Promise<AxiosResponse<ApiResponse<{
    suggestedType: string;
    message: any;
    reasoning: string;
  }>>> =>
    api.post('/ai-interactive/generate-message-from-products', data),

  // Get smart message type suggestion
  suggestMessageType: (data: {
    conversationId: string;
    intent: string;
  }): Promise<AxiosResponse<ApiResponse<{
    suggestedType: string;
    confidence: number;
    reasoning: string;
    templateSuggestion?: any;
  }>>> =>
    api.post('/ai-interactive/suggest-message-type', data),

  // Predict best time to send message
  predictBestTime: (data: {
    customerId: string;
    messageType?: 'promotional' | 'transactional' | 'support';
  }): Promise<AxiosResponse<ApiResponse<{
    recommendedTime: string;
    confidence: number;
    reasoning: string;
    alternativeTimes: string[];
    dayOfWeek: string;
    hourOfDay: number;
  }>>> =>
    api.post('/ai-interactive/predict-best-time', data),

  // Auto-categorize flow response
  categorizeFlowResponse: (data: {
    flowResponseId: string;
  }): Promise<AxiosResponse<ApiResponse<{
    category: string;
    subcategory: string;
    intent: string;
    leadScore: number;
    priority: 'high' | 'medium' | 'low';
    suggestedActions: string[];
    analysis: string;
  }>>> =>
    api.post('/ai-interactive/categorize-flow-response', data),

  // Bulk categorize flow responses
  bulkCategorizeFlowResponses: (data: {
    flowId: string;
    limit?: number;
  }): Promise<AxiosResponse<ApiResponse<{
    totalProcessed: number;
    successful: number;
    failed: number;
    results: any[];
  }>>> =>
    api.post('/ai-interactive/bulk-categorize-flow-responses', data),

  // Analyze interactive response sentiment
  analyzeInteractiveResponse: (data: {
    responseId: string;
  }): Promise<AxiosResponse<ApiResponse<{
    sentiment: 'positive' | 'negative' | 'neutral';
    engagement: 'high' | 'medium' | 'low';
    intent: string;
    confidence: number;
    insights: string[];
  }>>> =>
    api.post('/ai-interactive/analyze-interactive-response', data),

  // Get customer engagement analysis
  getCustomerEngagementAnalysis: (customerId: string): Promise<AxiosResponse<ApiResponse<{
    customerId: string;
    totalInteractions: number;
    interactiveMessageResponses: number;
    flowResponses: number;
    flowCompletionRate: number;
    messageTypePreferences: Array<{
      type: string;
      count: number;
      percentage: number;
    }>;
    averageResponseTimeMinutes: number;
    bestSendTime?: {
      dayOfWeek: string;
      hourOfDay: number;
      confidence: number;
    };
    engagementLevel: 'high' | 'medium' | 'low';
  }>>> =>
    api.get(`/ai-interactive/customer-engagement-analysis/${customerId}`),
}

export default api